
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Update scrolled state on window scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup in case component unmounts while menu is open
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { href: '/', label: 'HOME' },
    { href: '/o-firmie', label: 'O FIRMIE' },
    { href: '/uslugi', label: 'USŁUGI' },
    { href: '/realizacje', label: 'REALIZACJE' },
    { href: '/kontakt', label: 'KONTAKT' },
    { href: '/projekty-ue', label: 'PROJEKTY UE' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'bg-[#050505]/95 backdrop-blur-lg border-b border-[#00E5FF]/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/" className="flex items-center cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/39d6fbca-b681-4c48-84eb-ab94b5a74e78.png"
              alt="EUREKA"
              className="h-9 sm:h-12"
            />
          </a>

          {/* Desktop navigation links */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-white hover:text-[#00E5FF] transition-colors duration-300 text-xs xl:text-sm font-semibold tracking-wide cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Orbitron, sans-serif' }}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop call‑to‑action button */}
          <a
            href="/kontakt"
            className="hidden lg:block px-6 xl:px-8 py-2.5 xl:py-3 bg-[#00E5FF] text-[#050505] font-bold text-xs xl:text-sm hover:bg-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            ROZPOCZNIJ PROJEKT
          </a>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer relative z-50"
            aria-label="Menu"
          >
            <i
              className={`${
                mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
              } text-2xl transition-transform duration-300`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#050505] border-t border-[#00E5FF]/20 overflow-hidden"
          >
            <div className="px-4 sm:px-6 py-6 space-y-1">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-between py-3.5 text-white hover:text-[#00E5FF] transition-colors duration-300 text-sm font-semibold tracking-wide cursor-pointer border-b border-[#1A1A1A]"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}
                >
                  {link.label}
                  <i className="ri-arrow-right-s-line text-lg text-[#00E5FF]/40"></i>
                </motion.a>
              ))}

              {/* Mobile call‑to‑action button */}
              <motion.a
                href="/kontakt"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.35 }}
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full px-8 py-4 mt-4 bg-[#00E5FF] text-[#050505] font-bold text-sm text-center hover:bg-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Orbitron, sans-serif' }}
              >
                ROZPOCZNIJ PROJEKT
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
